// src/pages/Transactions/PointWithdrawalRequests.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const PointWithdrawalRequests = () => {
  const { user, updateUser } = useAuth(); // Add updateUser from useAuth
  const { role } = useParams();
  const location = useLocation();
  
  // Debug logging
  console.log('Current URL:', location.pathname);
  console.log('Role param from useParams:', role);
  console.log('Current admin role:', user?.role);
  console.log('Current user balance:', user?.walletBalance);
  
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({
    totalPending: 0,
    totalAmount: 0,
    pendingAmount: 0,
    approvedCount: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Map URL role to API role format
  const mapRoleToApiFormat = (urlRole) => {
    const roleMap = {
      'super-distributor': 'sub-admin',
      'distributor': 'distributor',
      'retailer': 'retailer',
      'user': 'user'
    };
    return roleMap[urlRole] || urlRole;
  };

  // Get the role in API format
  const getApiRole = () => {
    if (!role) return null;
    const mappedRole = mapRoleToApiFormat(role);
    console.log("Original role:", role, "Mapped to:", mappedRole);
    return mappedRole;
  };

  // Determine the API endpoint based on role
  const getApiEndpoint = () => {
    const apiRole = getApiRole();
    console.log("🚀 ~ getApiEndpoint ~ apiRole:", apiRole);
    console.log("🚀 ~ getApiEndpoint ~ role from params:", role);
    
    if (apiRole) {
      const endpoint = `/admin/withdrawals/${apiRole}/requests`;
      console.log("Using role-based endpoint:", endpoint);
      return endpoint;
    }
    console.log("Using default endpoint: /admin/withdrawals/requests");
    return '/admin/withdrawals/requests';
  };

  // Check if current admin can approve/reject this withdrawal request
  const canManageWithdrawal = (withdrawalUserRole) => {
    const adminRole = user?.role;
    console.log("🚀 ~ canManageWithdrawal ~ adminRole:", adminRole)
    const userRole = withdrawalUserRole;
    console.log("🚀 ~ canManageWithdrawal ~ userRole:", userRole)
    
    // Admin can manage sub-admin withdrawals
    if (adminRole === 'admin' && userRole === 'sub-admin') {
      return true;
    }
    // Sub-admin can manage distributor withdrawals
    if (adminRole === 'sub-admin' && userRole === 'distributor') {
      return true;
    }
    // Distributor can manage retailer withdrawals
    if (adminRole === 'distributor' && userRole === 'retailer') {
      return true;
    }
    // Retailer can manage user withdrawals
    if (adminRole === 'retailer' && userRole === 'user') {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [pagination.page, filter, role]);

  const fetchWithdrawalRequests = async () => {
    try {
      setLoading(true);
      const endpoint = getApiEndpoint();
      const params = { 
        page: pagination.page, 
        limit: pagination.limit,
        status: filter !== 'all' ? filter : undefined
      };
      
      console.log('Fetching withdrawals from:', endpoint, 'with params:', params);
      
      const response = await api.get(endpoint, { params });
      
      setRequests(response.data.withdrawals || []);
      setSummary(response.data.summary || {
        totalPending: 0,
        totalAmount: 0,
        pendingAmount: 0,
        approvedCount: 0
      });
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 1
      }));
      
      // Update current user's balance if it's returned in the response
      console.log("🚀 ~ fetchWithdrawalRequests ~ response.data:", response.data)
      if (response.data.currentUserBalance !== undefined) {
        if (updateUser) {
          updateUser({ walletBalance: response.data.currentUserBalance });
        }
      }
      
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    const result = await window.confirm('Are you sure you want to approve this withdrawal request? This will deduct the amount from the user\'s wallet.');
    if (!result) return;
    
    try {
      const apiRole = getApiRole();
      const endpoint = apiRole 
        ? `/admin/withdrawals/${apiRole}/${requestId}/approve`
        : `/admin/withdrawals/${requestId}/approve`;
      
      console.log('Approving from endpoint:', endpoint);
      
      const response = await api.put(endpoint);
      
      // Check if the response contains updated user information
      if (response.data.user && response.data.user.walletBalance !== undefined) {
        // If the approved withdrawal is for the current user (admin approving their own? unlikely)
        // Or if the response includes the admin's updated balance
        if (response.data.user.id === user?._id) {
          if (updateUser) {
            updateUser({ walletBalance: response.data.user.walletBalance });
          }
        }
      }
      
      // Also check if the response includes the admin's updated balance directly
      if (response.data.adminWalletBalance !== undefined) {
        if (updateUser) {
          updateUser({ walletBalance: response.data.adminWalletBalance });
        }
      }
      
      toast.success(response.data.message || 'Withdrawal approved successfully. Amount deducted from user wallet.');
      fetchWithdrawalRequests(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to approve withdrawal');
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      const apiRole = getApiRole();
      const endpoint = apiRole 
        ? `/admin/withdrawals/${apiRole}/${requestId}/reject`
        : `/admin/withdrawals/${requestId}/reject`;
      
      console.log('Rejecting from endpoint:', endpoint);
      
      const response = await api.put(endpoint, {
        remarks: rejectReason
      });
      toast.success(response.data.message || 'Withdrawal rejected successfully');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedRequest(null);
      fetchWithdrawalRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject withdrawal');
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '--';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleDisplay = (roleType) => {
    switch(roleType) {
      case 'admin':
        return 'Super Admin';
      case 'sub-admin':
        return 'Super Distributor';
      case 'distributor':
        return 'Distributor';
      case 'retailer':
        return 'Retailer';
      case 'user':
        return 'User';
      default:
        return roleType || 'User';
    }
  };

  // Get display role from URL role
  const getDisplayRole = () => {
    if (!role) return null;
    const roleMap = {
      'super-distributor': 'Super Distributor',
      'distributor': 'Distributor',
      'retailer': 'Retailer',
      'user': 'User'
    };
    return roleMap[role] || role;
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return 'badge badge-pending';
      case 'approved':
        return 'badge badge-active';
      case 'rejected':
        return 'badge badge-inactive';
      case 'cancelled':
        return 'badge badge-secondary';
      default:
        return 'badge badge-pending';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  // Get page title based on role
  const getPageTitle = () => {
    const displayRole = getDisplayRole();
    if (displayRole) {
      return `${displayRole} Withdrawal Requests`;
    }
    return 'Point Withdrawal Requests';
  };

  if (loading && requests.length === 0) {
    return (
      <div className="point-withdrawal-requests-page">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="point-withdrawal-requests-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">{getPageTitle()}</h1>
          
          {/* Display current admin's balance */}
          {user && (
            <div className="alert alert-info mt-3" style={{ background: '#300730', border: '1px solid #fff7ca' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Your Balance:</strong>
                </div>
                <div>
                  <span className="h5 mb-0 text-success">
                    {formatAmount(user.walletBalance || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="stat-card orange">
              <p>Pending Requests</p>
              <h3>{summary.totalPending || 0}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card purple">
              <p>Total Requested Points</p>
              <h3>{formatAmount(summary.totalAmount || 0)}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card blue">
              <p>Pending Points</p>
              <h3>{formatAmount(summary.pendingAmount || 0)}</h3>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="stat-card green">
              <p>Approved This Period</p>
              <h3>{summary.approvedCount || 0}</h3>
            </div>
          </div>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Withdrawal Requests</h2>
            <p className="text-muted mt-1 mb-0" style={{ color: '#fff7ca !important', fontSize: '14px' }}>
              Manage withdrawal requests from your downline users {getDisplayRole() ? `(${getDisplayRole()})` : ''}
            </p>
          </div>
          <div className="admin-card-body">
            {/* Filter Buttons */}
            <div className="mb-4">
              <div className="btn-group" role="group">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`btn ${filter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('pending')}
                >
                  Pending
                </button>
                <button 
                  className={`btn ${filter === 'approved' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('approved')}
                >
                  Approved
                </button>
                <button 
                  className={`btn ${filter === 'rejected' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('rejected')}
                >
                  Rejected
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-admin-one">
                <thead>
                  <tr>
                    <th>Sl No.</th>
                    <th>Request ID</th>
                    <th>User Details</th>
                    <th>Requested Points</th>
                    <th>Bank Details</th>
                    <th>Request Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length > 0 ? (
                    requests.map((request, index) => {
                      const canManage = canManageWithdrawal(request.user?.role);
                      const isPending = request.status === 'pending';
                      
                      return (
                        <tr key={request._id}>
                          <td>{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                          <td>
                            <small className="text-muted">{request.withdrawalId}</small>
                          </td>
                          <td>
                            <strong>{request.user?.fullName || request.user?.username || 'Unknown'}</strong>
                            <br />
                            <small className="text-muted">
                              {getRoleDisplay(request.user?.role)} | {request.user?.username}
                            </small>
                            {request.user?.phone && (
                              <div>
                                <small className="text-muted">📞 {request.user.phone}</small>
                              </div>
                            )}
                           </td>
                          <td className="text-danger fw-bold">
                            {formatAmount(request.amount)}
                           </td>
                           <td>
                            {request.bankDetails ? (
                              <>
                                <div><strong>{request.bankDetails.bankName}</strong></div>
                                <small>Acc: XXXX{request.bankDetails.accountNumber?.slice(-4)}</small>
                                <br />
                                <small>IFSC: {request.bankDetails.ifscCode}</small>
                                <br />
                                <small>{request.bankDetails.accountHolderName}</small>
                              </>
                            ) : (
                              '-'
                            )}
                           </td>
                           <td>
                            {formatDate(request.requestedAt || request.createdAt)}
                            {request.processedAt && (
                              <div>
                                <small className="text-muted">
                                  Processed: {formatDate(request.processedAt)}
                                </small>
                              </div>
                            )}
                           </td>
                           <td>
                            <span className={getStatusBadge(request.status)}>
                              {getStatusText(request.status)}
                            </span>
                            {request.remarks && request.status === 'rejected' && (
                              <div>
                                <small className="text-muted">Reason: {request.remarks}</small>
                              </div>
                            )}
                           </td>
                           <td>
                            {isPending && canManage && (
                              <div className="action-btn-group">
                                <button
                                  className="admin-action-btn approve"
                                  onClick={() => handleApprove(request._id)}
                                >
                                  Approve
                                </button>
                                <button
                                  className="admin-action-btn reject"
                                  onClick={() => openRejectModal(request)}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {isPending && !canManage && (
                              <span className="text-muted" style={{ fontSize: '12px' }}>
                                No permission
                              </span>
                            )}
                            {request.status === 'approved' && (
                              <span className="text-success">✓ Approved</span>
                            )}
                            {request.status === 'rejected' && (
                              <span className="text-danger">✗ Rejected</span>
                            )}
                           </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No withdrawal requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="admin-table-bottom">
                <div className="table-info">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} entries
                </div>
                <div className="table-pagination">
                  <button
                    className="page-btn"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="page-btn"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowRejectModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content admin-modal-one">
              <div className="modal-header">
                <h5 className="modal-title">Reject Withdrawal Request</h5>
                <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to reject this withdrawal request?</p>
                <div className="mb-3">
                  <label className="form-label">Amount: <strong>{formatAmount(selectedRequest.amount)}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">User: <strong>{selectedRequest.user?.username}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason for rejection <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control admin-input-one"
                    rows="3"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting this withdrawal request..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={() => handleReject(selectedRequest._id)}
                  disabled={!rejectReason.trim()}
                >
                  Reject Withdrawal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointWithdrawalRequests;